import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Search, 
  FileText, 
  Users, 
  Building2, 
  Car,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  ExternalLink
} from 'lucide-react';

const Validaciones = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('person');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastValidationData, setLastValidationData] = useState(null);
  
  const [personForm, setPersonForm] = useState({
    documentType: 'CC',
    documentNumber: '',
    firstName: '',
    lastName: ''
  });

  const [companyForm, setCompanyForm] = useState({
    nit: '',
    companyName: '',
    verificationDigit: ''
  });
  const [companyLookup, setCompanyLookup] = useState(null);
  const [companyLookupError, setCompanyLookupError] = useState('');
  const [companyLookingUp, setCompanyLookingUp] = useState(false);
  const [companyFuenteNombre, setCompanyFuenteNombre] = useState('');

  const [vehicleForm, setVehicleForm] = useState({
    plate: '',
    vehicleType: 'automovil',
    ownerDocType: 'CC',
    ownerDocNumber: '',
  });

  const handlePersonSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Real API call
    try {
      const user = JSON.parse(localStorage.getItem('nika-user') || '{}');
      const token = localStorage.getItem('nika-auth-token');
      
      if (!user.id || !token) {
        toast({
          title: "Error de autenticación",
          description: "Por favor, inicia sesión para realizar validaciones."
        });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/validations/person`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          document_type: personForm.documentType,
          document_number: personForm.documentNumber,
          first_name: personForm.firstName,
          last_name: personForm.lastName
        })
      });

      const data = await response.json();
      
      if (response.ok && data.result) {
        // Use the name resolved from Procuraduría / Policía / Registraduría
        // (if user did not provide one). Source label comes from backend.
        const NAME_SOURCE_LABELS = {
          procuraduria: 'Obtenido de Procuraduría',
          policia: 'Obtenido de Policía Nacional',
          rnec_registraduria: 'Obtenido de Registraduría',
          verifik_registraduria: 'Obtenido de Registraduría',
          user_input: 'Nombre ingresado por el usuario, no verificado contra el documento',
        };
        const raw = data.result.raw_data || {};
        const resolvedFirst = raw.first_name || personForm.firstName || '';
        const resolvedLast = raw.last_name || personForm.lastName || '';
        const resolvedFull = raw.full_name
          || ((personForm.firstName || personForm.lastName)
            ? `${personForm.firstName} ${personForm.lastName}`.trim()
            : '');

        const formData = {
          "Tipo de Documento": personForm.documentType,
          "Número de Documento": personForm.documentNumber,
          "Nombres": resolvedFirst || '—',
          "Apellidos": resolvedLast || '—',
          "Nombre Completo": resolvedFull || '—',
          "Fuente del nombre": raw.name_origin_label || NAME_SOURCE_LABELS[raw.name_source] || '',
        };

        toast({
          title: "Validación completada",
          description: resolvedFull
            ? `Consulta exitosa: ${resolvedFull}`
            : "La consulta se realizó exitosamente."
        });

        // Auto-navigate to the full report so the user always sees the result
        // (avoids the "blank screen until refresh" bug that was caused by the
        // result Card being rendered far below on a long form page).
        navigate('/reporte-completo', {
          state: {
            result: { validation_id: data.validation_id, ...data.result },
            tipo: 'person',
            datos: formData,
            validation_id: data.validation_id,
          },
        });
        return;
      } else {
        throw new Error(data.detail || 'Error en la validación');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error en validación",
        description: error.message || "No se pudo completar la validación."
      });
    } finally {
      setLoading(false);
    }
  };

  const nitDigitsOnly = (nit) => {
    const raw = (nit || '').trim();
    const before = raw.includes('-') ? raw.split('-')[0] : raw;
    return before.replace(/\D/g, '');
  };

  const aplicarNombreFuente = (nombre) => {
    setCompanyFuenteNombre(nombre || '');
    setCompanyForm((prev) => ({ ...prev, companyName: nombre || '' }));
  };

  const buscarEmpresaPorNit = async (nitValue) => {
    const digits = nitDigitsOnly(nitValue);
    if (digits.length < 6) return;
    const token = localStorage.getItem('nika-auth-token');
    if (!token) {
      toast({
        title: "Error de autenticación",
        description: "Por favor, inicia sesión para realizar validaciones."
      });
      return;
    }
    setCompanyLookingUp(true);
    setCompanyLookupError('');
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/companies/lookup?nit=${encodeURIComponent(nitValue)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await r.json().catch(() => ({}));
      if (r.status === 400) {
        setCompanyLookup(null);
        setCompanyFuenteNombre('');
        setCompanyLookupError('NIT no válido');
        return;
      }
      if (!r.ok) {
        setCompanyLookup({ found: false, source_unavailable: true, matches: [] });
        return;
      }
      setCompanyLookup(data);
      const autoNombre = data.autocompletar_seguro && data.matches?.[0]?.razon_social
        ? data.matches[0].razon_social
        : null;
      if (autoNombre) setCompanyFuenteNombre(autoNombre);
      setCompanyForm((prev) => ({
        ...prev,
        verificationDigit: data.dv_calculado != null ? String(data.dv_calculado) : prev.verificationDigit,
        companyName: autoNombre != null ? autoNombre : prev.companyName,
      }));
    } catch (e) {
      setCompanyLookup({ found: false, source_unavailable: true, matches: [] });
    } finally {
      setCompanyLookingUp(false);
    }
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = {
      "NIT": `${companyForm.nit}${companyForm.verificationDigit ? '-' + companyForm.verificationDigit : ''}`,
      "Razón Social": companyForm.companyName,
      "Dígito de Verificación": companyForm.verificationDigit || 'Calculado automáticamente'
    };
    
    setLastValidationData({ tipo: 'company', datos: formData });
    
    try {
      const user = JSON.parse(localStorage.getItem('nika-user') || '{}');
      const token = localStorage.getItem('nika-auth-token');
      
      if (!user.id || !token) {
        toast({
          title: "Error de autenticación",
          description: "Por favor, inicia sesión para realizar validaciones."
        });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/validations/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nit: companyForm.nit,
          company_name: companyForm.companyName,
          verification_digit: companyForm.verificationDigit
        })
      });

      const data = await response.json();
      
      if (response.ok && data.result) {
        toast({
          title: "Validación completada",
          description: "La consulta empresarial se realizó exitosamente."
        });
        navigate('/reporte-completo', {
          state: {
            result: { validation_id: data.validation_id, ...data.result },
            tipo: 'company',
            datos: lastValidationData?.datos || { "NIT": companyForm.nit, "Razón Social": companyForm.companyName },
            validation_id: data.validation_id,
          },
        });
        return;
      } else {
        throw new Error(data.detail || 'Error en la validación');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error en validación",
        description: error.message || "No se pudo completar la validación."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = {
      "Placa": vehicleForm.plate.toUpperCase(),
      "Tipo de Vehículo": vehicleForm.vehicleType.charAt(0).toUpperCase() + vehicleForm.vehicleType.slice(1),
      ...(vehicleForm.ownerDocNumber ? {
        "Tipo Doc Propietario": vehicleForm.ownerDocType,
        "Cédula Propietario": vehicleForm.ownerDocNumber,
      } : {}),
    };
    
    setLastValidationData({ tipo: 'vehicle', datos: formData });
    
    try {
      const user = JSON.parse(localStorage.getItem('nika-user') || '{}');
      const token = localStorage.getItem('nika-auth-token');
      
      if (!user.id || !token) {
        toast({
          title: "Error de autenticación",
          description: "Por favor, inicia sesión para realizar validaciones."
        });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/validations/vehicle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plate: vehicleForm.plate.toUpperCase(),
          vehicle_type: vehicleForm.vehicleType,
          ...(vehicleForm.ownerDocNumber ? {
            owner_document_type: vehicleForm.ownerDocType,
            owner_document_number: vehicleForm.ownerDocNumber.trim(),
          } : {}),
        })
      });

      const data = await response.json();
      
      if (response.ok && data.result) {
        toast({
          title: "Validación completada",
          description: "La consulta vehicular se realizó exitosamente."
        });
        navigate('/reporte-completo', {
          state: {
            result: { validation_id: data.validation_id, ...data.result },
            tipo: 'vehicle',
            datos: formData,
            validation_id: data.validation_id,
          },
        });
        return;
      } else {
        throw new Error(data.detail || 'Error en la validación');
      }
      
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error en validación",
        description: error.message || "No se pudo completar la validación."
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'clear': 
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'alert':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleViewFullReport = () => {
    if (result && lastValidationData) {
      navigate('/reporte-completo', {
        state: {
          result: result,
          tipo: lastValidationData.tipo,
          datos: lastValidationData.datos
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {loading && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="validation-loading-overlay">
          <div className="bg-white rounded-lg p-8 max-w-md text-center shadow-2xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Consultando fuentes oficiales…</h3>
            <p className="text-sm text-gray-600">
              Estamos verificando Registraduría, Policía, Procuraduría, Contraloría, OFAC, ONU, EU, INTERPOL, DEA, FBI y Banco Mundial.
              Esto puede tardar 30 a 60 segundos.
            </p>
            <p className="text-xs text-gray-400 mt-3">No cierres esta ventana ni recargues la página.</p>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4 py-8">
        
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Validaciones
          </h1>
          <p className="text-gray-600">
            Realiza consultas de validación para personas, empresas y vehículos
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Search className="h-5 w-5" />
                  <span>Nueva Validación</span>
                </CardTitle>
                <CardDescription>
                  Selecciona el tipo de validación y completa los datos requeridos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="person" className="flex items-center space-x-2">
                      <Users className="h-4 w-4" />
                      <span>Persona</span>
                    </TabsTrigger>
                    <TabsTrigger value="company" className="flex items-center space-x-2">
                      <Building2 className="h-4 w-4" />
                      <span>Empresa</span>
                    </TabsTrigger>
                    <TabsTrigger value="vehicle" className="flex items-center space-x-2">
                      <Car className="h-4 w-4" />
                      <span>Vehículo</span>
                    </TabsTrigger>
                  </TabsList>

                  {/* Person Validation - Simplified: solo cédula es requerida */}
                  <TabsContent value="person" className="mt-6">
                    <form onSubmit={handlePersonSubmit} className="space-y-4" data-testid="person-validation-form">
                      <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                        <strong>Consulta rápida:</strong> ingresa solo el número de cédula. El nombre se obtiene automáticamente desde la Registraduría Nacional.
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="documentType">Tipo de Documento</Label>
                          <Select 
                            value={personForm.documentType} 
                            onValueChange={(value) => setPersonForm({...personForm, documentType: value})}
                          >
                            <SelectTrigger data-testid="person-document-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="CC">Cédula de Ciudadanía</SelectItem>
                              <SelectItem value="TI">Tarjeta de Identidad (menor 16-18 años)</SelectItem>
                              <SelectItem value="CE">Cédula de Extranjería</SelectItem>
                              <SelectItem value="PPT">Permiso de Protección Temporal (PPT)</SelectItem>
                              <SelectItem value="PA">Pasaporte</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-2 space-y-2">
                          <Label htmlFor="documentNumber">Número de Documento</Label>
                          <Input
                            id="documentNumber"
                            placeholder="Ej: 1020456789"
                            value={personForm.documentNumber}
                            onChange={(e) => setPersonForm({...personForm, documentNumber: e.target.value.replace(/\D/g,'')})}
                            inputMode="numeric"
                            autoFocus
                            required
                            data-testid="person-document-number"
                          />
                        </div>
                      </div>
                      <details className="group rounded-lg border border-gray-200 bg-white">
                        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="person-name-toggle">
                          <span className="group-open:hidden">+ Agregar nombre y apellido (opcional)</span>
                          <span className="hidden group-open:inline">− Ocultar campos de nombre</span>
                        </summary>
                        <p className="px-3 pb-2 text-xs text-gray-500" data-testid="person-name-hint">
                          Para el cribado en listas restrictivas internacionales escribe nombre y apellido.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-3 pb-3 pt-1">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">Nombres (opcional)</Label>
                            <Input
                              id="firstName"
                              placeholder="Se traerá de Registraduría si lo dejas vacío"
                              value={personForm.firstName}
                              onChange={(e) => setPersonForm({...personForm, firstName: e.target.value})}
                              data-testid="person-first-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Apellidos (opcional)</Label>
                            <Input
                              id="lastName"
                              placeholder="Se traerá de Registraduría si lo dejas vacío"
                              value={personForm.lastName}
                              onChange={(e) => setPersonForm({...personForm, lastName: e.target.value})}
                              data-testid="person-last-name"
                            />
                          </div>
                        </div>
                      </details>
                      <Button type="submit" className="w-full" disabled={loading} data-testid="person-submit-btn">
                        {loading ? 'Validando...' : 'Validar Persona'}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Company Validation */}
                  <TabsContent value="company" className="mt-6">
                    <form onSubmit={handleCompanySubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 space-y-2">
                          <Label htmlFor="nit">NIT</Label>
                          <div className="flex gap-2">
                            <Input
                              id="nit"
                              placeholder="Ej: 900123456"
                              value={companyForm.nit}
                              onChange={(e) => {
                                setCompanyForm({...companyForm, nit: e.target.value, verificationDigit: ''});
                                setCompanyLookup(null);
                                setCompanyLookupError('');
                                setCompanyFuenteNombre('');
                              }}
                              onBlur={() => {
                                if (nitDigitsOnly(companyForm.nit).length >= 6) {
                                  buscarEmpresaPorNit(companyForm.nit);
                                }
                              }}
                              required
                            />
                            <Button
                              type="button"
                              variant="outline"
                              disabled={companyLookingUp || nitDigitsOnly(companyForm.nit).length < 6}
                              onClick={() => buscarEmpresaPorNit(companyForm.nit)}
                            >
                              {companyLookingUp ? 'Buscando…' : 'Buscar'}
                            </Button>
                          </div>
                          {companyLookupError && (
                            <p className="text-xs text-red-600">{companyLookupError}</p>
                          )}
                          {companyLookup?.nit_con_dv && !companyLookupError && (
                            <p className="text-xs text-gray-700">NIT: {companyLookup.nit_con_dv}</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="verificationDigit">DV (opcional)</Label>
                          <Input
                            id="verificationDigit"
                            placeholder="Auto"
                            maxLength="1"
                            readOnly={Boolean(companyLookup?.nit_con_dv)}
                            value={companyForm.verificationDigit}
                            onChange={(e) => setCompanyForm({...companyForm, verificationDigit: e.target.value})}
                          />
                          <p className="text-xs text-gray-500">Si lo dejas vacío, se calcula automáticamente.</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="companyName">Razón Social</Label>
                        <Input
                          id="companyName"
                          placeholder="Nombre de la empresa"
                          value={companyForm.companyName}
                          onChange={(e) => setCompanyForm({...companyForm, companyName: e.target.value})}
                        />
                        {!companyForm.companyName.trim() && (
                          <p className="text-xs text-gray-500">
                            Si dejas la razón social vacía, la verificación en listas por nombre no se hará completa.
                          </p>
                        )}
                        {companyFuenteNombre && companyForm.companyName.trim()
                          && companyForm.companyName.trim() !== companyFuenteNombre.trim() && (
                          <p className="text-xs text-amber-700">
                            Difiere del registro: {companyFuenteNombre}
                          </p>
                        )}
                      </div>
                      {companyLookup && !companyLookupError && companyLookup.autocompletar_seguro && companyLookup.matches?.[0] && (
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
                          <div><span className="text-gray-500">Razón social:</span> {companyLookup.matches[0].razon_social}</div>
                          <div><span className="text-gray-500">Estado de la matrícula:</span> {companyLookup.matches[0].estado_matricula}</div>
                          <div><span className="text-gray-500">Cámara de comercio:</span> {companyLookup.matches[0].camara_comercio}</div>
                        </div>
                      )}
                      {companyLookup && !companyLookupError && companyLookup.ambiguo && (
                        <div className="rounded-md border border-gray-200 p-3 space-y-2">
                          <p className="text-xs text-gray-600">Hay más de un registro. Elige uno:</p>
                          {(companyLookup.matches || []).map((m, i) => (
                            <button
                              type="button"
                              key={i}
                              className="w-full text-left text-sm border rounded p-2 hover:bg-gray-50"
                              onClick={() => aplicarNombreFuente(m.razon_social)}
                            >
                              <div className="font-medium">{m.razon_social}</div>
                              <div className="text-xs text-gray-500">{m.estado_matricula} · {m.camara_comercio}</div>
                            </button>
                          ))}
                        </div>
                      )}
                      {companyLookup && !companyLookupError && companyLookup.found
                        && !companyLookup.autocompletar_seguro && !companyLookup.ambiguo && (
                        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 space-y-2">
                          <p className="text-sm">{companyLookup.matches?.[0]?.razon_social}</p>
                          <p className="text-xs text-yellow-800">Estado: {companyLookup.matches?.[0]?.estado_matricula}</p>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => aplicarNombreFuente(companyLookup.matches[0].razon_social)}
                          >
                            Usar este nombre
                          </Button>
                        </div>
                      )}
                      {companyLookup && !companyLookupError
                        && (companyLookup.source_unavailable || !companyLookup.found)
                        && !companyLookup.ambiguo && (
                        <p className="text-xs text-gray-600">
                          No se encontró la empresa en el registro. Puedes escribir la razón social manualmente.
                        </p>
                      )}
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? 'Validando...' : 'Validar Empresa'}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Vehicle Validation */}
                  <TabsContent value="vehicle" className="mt-6">
                    <form onSubmit={handleVehicleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="plate">Placa</Label>
                          <Input
                            id="plate"
                            placeholder="Ej: ABC123"
                            value={vehicleForm.plate}
                            onChange={(e) => setVehicleForm({...vehicleForm, plate: e.target.value.toUpperCase()})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="vehicleType">Tipo de Vehículo</Label>
                          <Select 
                            value={vehicleForm.vehicleType} 
                            onValueChange={(value) => setVehicleForm({...vehicleForm, vehicleType: value})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="automovil">Automóvil</SelectItem>
                              <SelectItem value="motocicleta">Motocicleta</SelectItem>
                              <SelectItem value="camion">Camión</SelectItem>
                              <SelectItem value="bus">Bus</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Optional owner document — required to unlock SOAT/RTM/owner data */}
                      <div className="border border-blue-100 bg-blue-50/40 rounded-lg p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="text-xs text-blue-800 leading-relaxed">
                            <strong>Opcional — Cédula del propietario.</strong>{' '}
                            Si la conoces, ingresa abajo para obtener datos certificados del propietario,
                            estado SOAT y vencimiento de la Revisión Técnico-Mecánica. Sin ella solo se
                            consultan datos del vehículo (Fasecolda).
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="ownerDocType">Tipo Documento</Label>
                            <Select
                              value={vehicleForm.ownerDocType}
                              onValueChange={(value) => setVehicleForm({...vehicleForm, ownerDocType: value})}
                            >
                              <SelectTrigger data-testid="vehicle-owner-doctype">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="CC">Cédula de Ciudadanía</SelectItem>
                                <SelectItem value="TI">Tarjeta de Identidad</SelectItem>
                                <SelectItem value="CE">Cédula de Extranjería</SelectItem>
                                <SelectItem value="PPT">Permiso de Protección Temporal</SelectItem>
                                <SelectItem value="PA">Pasaporte</SelectItem>
                                <SelectItem value="NIT">NIT (persona jurídica)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="ownerDocNumber">Número de Documento del Propietario</Label>
                            <Input
                              id="ownerDocNumber"
                              placeholder="Opcional — desbloquea datos certificados"
                              value={vehicleForm.ownerDocNumber}
                              onChange={(e) => setVehicleForm({...vehicleForm, ownerDocNumber: e.target.value})}
                              data-testid="vehicle-owner-docnumber"
                            />
                          </div>
                        </div>
                      </div>

                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? 'Validando...' : 'Validar Vehículo'}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Results Section */}
          <div>
            {result ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    Resultado de Validación
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  
                  {/* Score */}
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className={`text-3xl font-bold ${getScoreColor(result.score)}`}>
                      {result.score}/100
                    </div>
                    <div className="text-sm text-gray-600">Puntaje de Confianza</div>
                  </div>

                  {/* Risk Level */}
                  <div className="text-center">
                    <Badge className={
                      result.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                      result.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      result.risk_level === 'high' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      Riesgo {result.risk_level === 'low' ? 'Bajo' : 
                               result.risk_level === 'medium' ? 'Medio' : 
                               result.risk_level === 'high' ? 'Alto' : 'Crítico'}
                    </Badge>
                  </div>

                  {/* Validation Info */}
                  {result.validation_id && (
                    <div className="text-xs text-gray-500 text-center">
                      ID de Validación: {result.validation_id}
                    </div>
                  )}

                  {/* Findings Summary */}
                  <div className="space-y-3">
                    <div className="font-medium text-gray-900">
                      Resumen de Hallazgos ({result.findings?.filter(f => f.status !== 'incomplete').length || 0}):
                    </div>
                    {result.findings?.filter(f => f.status !== 'incomplete').slice(0, 3).map((finding, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                        {getStatusIcon(finding.status)}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {({
                              identity: 'Identidad',
                              judicial: 'Antecedentes / Disciplinario',
                              restrictive_lists: 'Listas Restrictivas',
                              financial: 'Fiscal / Financiero',
                              registry: 'Registros Oficiales',
                              soat: 'SOAT',
                              tecno: 'Tecnomecánica',
                              fines: 'Multas',
                            }[finding.type] || finding.type.replace(/_/g, ' '))}
                          </div>
                          <div className="text-sm text-gray-600">
                            {finding.message}
                          </div>
                        </div>
                      </div>
                    ))}
                    {result.findings?.filter(f => f.status !== 'incomplete').length > 3 && (
                      <div className="text-sm text-gray-500 text-center">
                        +{result.findings.filter(f => f.status !== 'incomplete').length - 3} hallazgo(s) adicional(es)
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700" 
                      onClick={handleViewFullReport}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Ver Reporte Completo
                    </Button>
                    
                    <Button className="w-full" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Descargar PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <div className="text-lg font-medium text-gray-900 mb-2">
                    Sin resultados
                  </div>
                  <div className="text-gray-600">
                    Completa el formulario para ver los resultados de la validación
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Validaciones;